import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PreRegistration } from '@cloudsign/shared';
import type { Site } from '@cloudsign/shared';
import { X } from 'lucide-react';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { Alert, Button, CheckboxField, ConfirmButton, Spinner, TextField } from '../../components/ui';
import { ErrorPanel, errorMessage } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { TimeInput, isTimeOfDay, isValidEmail } from '../../components/phase2';
import { useModuleOn } from '../people/api';
import { RaisePermitDrawer } from '../permits/RaisePermitDrawer';
import { StatusLabel, progressItems } from './Progress';
import { preKey } from './api';
import { localToUtcIso, utcToLocal } from '../../lib/zoneTime';

const OPEN = ['expected', 'pending_approval'] as const;

/** One pre-registration: progress, email status, and the things you can do to it. */
export function DetailDrawer({ id, site, onClose }: { id: string; site: Site | undefined; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const canManage = useCan('preregistrations.manage');
  const canEditOwn = useCan('today.sign_in_out');
  const permitsOn = useModuleOn('permits');
  const canRaisePermit = useCan('permits.raise');
  const [raising, setRaising] = useState(false);
  const zone = site?.timeZone ?? 'Europe/London';
  const q = useQuery({ queryKey: [...preKey, 'one', id], queryFn: () => api.get(`/pre-registration/${id}`, { schema: PreRegistration }) });
  const [editing, setEditing] = useState(false);
  const [notify, setNotify] = useState(true);
  const [lateDate, setLateDate] = useState('');
  const [lateTime, setLateTime] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const done = (message: string) => (r: PreRegistration) => {
    qc.setQueryData([...preKey, 'one', id], r);
    void qc.invalidateQueries({ queryKey: preKey });
    toast(message);
  };
  const resend = useMutation({ mutationFn: () => api.post(`/pre-registration/${id}/resend`, { schema: PreRegistration }), onSuccess: done(t('prereg.drawer.resent')) });
  const cancel = useMutation({
    mutationFn: () => api.post(`/pre-registration/${id}/cancel`, { body: { notify }, schema: PreRegistration }),
    onSuccess: done(t('prereg.drawer.cancelled')),
  });
  const answer = useMutation({
    mutationFn: (body: { answer: 'running_late' | 'cancelled'; newExpectedAt: string | null }) => api.post(`/pre-registration/${id}/host-answer`, { body, schema: PreRegistration }),
    onSuccess: done(t('prereg.drawer.answered')),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/pre-registration/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: preKey });
      toast(t('prereg.drawer.deleted'));
      onClose();
    },
  });

  const p = q.data;
  const open = p !== undefined && (OPEN as readonly string[]).includes(p.status);
  const canChange = canManage || canEditOwn;
  const error = resend.error ?? cancel.error ?? answer.error ?? remove.error;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/40">
      <aside role="dialog" aria-modal="true" aria-label={t('prereg.drawer.title')} className="h-full w-full max-w-lg overflow-y-auto bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900">{p ? p.visitorName : t('prereg.drawer.title')}</h2>
          <button type="button" onClick={onClose} aria-label={t('prereg.drawer.close')} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100">
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
        {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
        {!p && !q.isError ? <Spinner /> : null}
        {p ? (
          <div className="space-y-6">
            {error ? <Alert>{errorMessage(error)}</Alert> : null}
            <div className="flex flex-wrap items-center gap-2">
              <StatusLabel status={p.status} />
              <span className="text-sm text-slate-700">{formatDateTime(p.expectedAt, zone)}</span>
            </div>

            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              {(
                [
                  ['prereg.field.email', p.visitorEmail],
                  ['prereg.field.phone', p.visitorPhone],
                  ['prereg.field.company', p.visitorCompany],
                  ['prereg.field.host', p.hostName],
                  ['prereg.field.site', site?.name ?? null],
                  ['prereg.field.until', p.expectedUntil ? formatDateTime(p.expectedUntil, zone) : null],
                  ['prereg.field.notes', p.notes === '' ? null : p.notes],
                  ['prereg.drawer.createdBy', p.createdByLabel],
                ] as const
              ).map(([k, v]) =>
                v ? (
                  <div key={k} className="contents">
                    <dt className="text-slate-600">{t(k)}</dt>
                    <dd className="break-words text-slate-900">{v}</dd>
                  </div>
                ) : null,
              )}
            </dl>

            <section aria-labelledby="pr-progress">
              <h3 id="pr-progress" className="mb-2 text-sm font-semibold text-slate-900">
                {t('prereg.drawer.progress')}
              </h3>
              <ul className="space-y-1.5 text-sm">
                {progressItems(p.progress).map((i) => (
                  <li key={i.key} className={`flex items-center gap-2 ${i.done ? 'text-green-800' : 'text-slate-600'}`}>
                    {i.icon}
                    <span>
                      {i.label}: <span className="font-medium">{i.done ? t('prereg.progress.done') : t('prereg.progress.notYet')}</span>
                      {i.detail ? ` (${i.detail})` : ''}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-slate-600">
                {p.progress.lastActivityAt ? t('prereg.drawer.lastActivity', { when: formatDateTime(p.progress.lastActivityAt, zone) }) : t('prereg.drawer.noActivity')}
              </p>
            </section>

            <section aria-labelledby="pr-email" className="space-y-2">
              <h3 id="pr-email" className="text-sm font-semibold text-slate-900">
                {t('prereg.drawer.email')}
              </h3>
              <p className="text-sm text-slate-800">{t(`prereg.emailStatus.${p.emailStatus}`)}</p>
              {open && canChange && p.visitorEmail ? (
                <Button disabled={resend.isPending} onClick={() => resend.mutate()}>
                  {t('prereg.drawer.resend')}
                </Button>
              ) : null}
            </section>

            {p.noShowAlertedAt && p.status === 'expected' && canChange ? (
              <section aria-labelledby="pr-noshow" className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
                <h3 id="pr-noshow" className="text-sm font-semibold text-amber-900">
                  {t('prereg.drawer.noShowAlert')}
                </h3>
                {p.hostAnswer ? <p className="text-sm">{t(`prereg.hostAnswer.${p.hostAnswer}`)}</p> : null}
                <div className="flex flex-wrap items-end gap-2">
                  <TextField label={t('prereg.drawer.newDate')} type="date" value={lateDate} onChange={(e) => setLateDate(e.target.value)} />
                  <TimeInput label={t('prereg.drawer.newTime')} value={lateTime} onChange={setLateTime} />
                  <Button
                    disabled={answer.isPending}
                    onClick={() =>
                      answer.mutate({
                        answer: 'running_late',
                        newExpectedAt: lateDate !== '' && isTimeOfDay(lateTime) ? localToUtcIso(lateDate, lateTime, zone) : null,
                      })
                    }
                  >
                    {t('prereg.drawer.runningLate')}
                  </Button>
                  <Button disabled={answer.isPending} onClick={() => answer.mutate({ answer: 'cancelled', newExpectedAt: null })}>
                    {t('prereg.drawer.markCancelled')}
                  </Button>
                </div>
              </section>
            ) : null}

            {open && canChange ? (
              <section aria-labelledby="pr-edit" className="space-y-3">
                <h3 id="pr-edit" className="text-sm font-semibold text-slate-900">
                  {t('prereg.drawer.change')}
                </h3>
                {editing ? <EditForm p={p} zone={zone} onDone={(r) => { done(t('prereg.drawer.saved'))(r); setEditing(false); }} onCancel={() => setEditing(false)} /> : <Button onClick={() => setEditing(true)}>{t('prereg.drawer.edit')}</Button>}
                <div className="space-y-2 border-t border-slate-200 pt-3">
                  <CheckboxField label={t('prereg.drawer.notifyVisitor')} checked={notify} onChange={(e) => setNotify(e.target.checked)} />
                  <ConfirmButton label={t('prereg.drawer.cancel')} pending={cancel.isPending} onConfirm={() => cancel.mutate()} />
                </div>
              </section>
            ) : null}

            {permitsOn && canRaisePermit && open ? (
              <div className="border-t border-slate-200 pt-3">
                <Button onClick={() => setRaising(true)}>{t('w23.permit.raise')}</Button>
              </div>
            ) : null}
            {raising ? (
              <RaisePermitDrawer siteId={p.siteId} preRegistrationId={p.id} memberId={p.memberId} holderName={p.visitorName} onClose={() => setRaising(false)} />
            ) : null}

            {canManage && p.status !== 'arrived' ? (
              <div className="border-t border-slate-200 pt-3">
                <ConfirmButton label={t('prereg.drawer.delete')} pending={remove.isPending} onConfirm={() => remove.mutate()} />
              </div>
            ) : null}
          </div>
        ) : null}
      </aside>
    </div>
  );
}

function EditForm({ p, zone, onDone, onCancel }: { p: PreRegistration; zone: string; onDone: (r: PreRegistration) => void; onCancel: () => void }) {
  const start = utcToLocal(p.expectedAt, zone);
  const [name, setName] = useState(p.visitorName);
  const [email, setEmail] = useState(p.visitorEmail ?? '');
  const [phone, setPhone] = useState(p.visitorPhone ?? '');
  const [company, setCompany] = useState(p.visitorCompany ?? '');
  const [date, setDate] = useState(start.date);
  const [time, setTime] = useState(start.time);
  const [resend, setResend] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/pre-registration/${p.id}`, {
        body: {
          visitorName: name.trim(),
          visitorEmail: email.trim() === '' ? null : email.trim(),
          visitorPhone: phone.trim() === '' ? null : phone.trim(),
          visitorCompany: company.trim() === '' ? null : company.trim(),
          expectedAt: localToUtcIso(date, time, zone),
          resendEmail: resend,
        },
        schema: PreRegistration,
      }),
    onSuccess: onDone,
  });
  const emailBad = email.trim() !== '' && !isValidEmail(email.trim());
  const invalid = name.trim() === '' || emailBad || date === '' || !isTimeOfDay(time);
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!invalid) save.mutate();
      }}
    >
      {save.error ? <Alert>{errorMessage(save.error)}</Alert> : null}
      <TextField label={t('prereg.field.name')} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} error={name.trim() === '' ? t('prereg.create.nameRequired') : undefined} />
      <TextField label={t('prereg.field.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={emailBad ? t('prereg.create.emailInvalid') : undefined} />
      <TextField label={t('prereg.field.phone')} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
      <TextField label={t('prereg.field.company')} value={company} maxLength={120} onChange={(e) => setCompany(e.target.value)} />
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label={t('prereg.field.date')} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <TimeInput label={t('prereg.field.time')} value={time} onChange={setTime} hint={t('prereg.create.zone', { zone })} />
      </div>
      <CheckboxField label={t('prereg.drawer.resendOnSave')} hint={t('prereg.drawer.resendOnSaveHint')} checked={resend} onChange={(e) => setResend(e.target.checked)} />
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={save.isPending || invalid}>
          {t('common.save')}
        </Button>
        <Button type="button" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}
